import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"
import { ArrowRight, Award, Download, RotateCcw } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { MemberLayout } from "../components/MemberLayout"
import { Button } from "@/components/ui/button"
import { TrackRail } from "@/components/track/TrackRail"

// The overview for a guided track (Sequential or Certifying): every step, the
// certificate, and Continue. Lessons open in the track player
// (plan 2026-09-23-002). Locks are enforced on the server; this only draws them.

function CertifiedBanner({ certification }) {
  return (
    <div className="rounded-xl bg-green-600 text-white p-5 flex flex-wrap items-center gap-4">
      <Award className="h-10 w-10 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-display text-2xl font-semibold">You are certified</p>
        <p className="text-sm opacity-90">
          Earned {certification.earnedOn}
          {certification.scorePercent != null && ` · final score ${certification.scorePercent}%`} · No.{" "}
          {certification.certificateNumber}
        </p>
      </div>
      <Button asChild variant="secondary" className="min-h-[44px]">
        <a href={certification.pdfUrl} target="_blank" rel="noopener noreferrer">
          <Download className="h-4 w-4" />
          Download certificate
        </a>
      </Button>
    </div>
  )
}

// Admins test a path as themselves and then start over, with no second login.
// The dialog says exactly what is cleared; nothing happens until they confirm.
function StartOver({ trackId }) {
  const [open, setOpen] = useState(false)
  const [working, setWorking] = useState(false)

  const confirm = () => {
    setWorking(true)
    router.delete(`/app/member/learning_tracks/${trackId}/progress`, {
      preserveScroll: true,
      onFinish: () => {
        setWorking(false)
        setOpen(false)
      },
    })
  }

  return (
    <>
      <Button variant="outline" className="w-full min-h-[44px] text-zinc-600" onClick={() => setOpen(true)}>
        <RotateCcw className="h-4 w-4" />
        Start over (testing)
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-white border-zinc-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Start this path over?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-zinc-600">
                <p>This is for testing. It clears your own progress on the courses in this path:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>every quiz attempt and score</li>
                  <li>every lesson you marked complete</li>
                  <li>your written answers</li>
                  <li>your certificate, if you earned one</li>
                </ul>
                <p>
                  Other people are not affected. If a course in this path is also in another path, your progress
                  on it is cleared there too. You cannot undo this.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={working}
              onClick={(e) => {
                e.preventDefault()
                confirm()
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {working ? "Clearing..." : "Yes, clear my progress"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default function TrackShow({
  currentUser,
  track,
  steps = [],
  progress,
  continueHref = null,
  certification = null,
  canStartOver = false,
}) {
  const nextStep = steps.find((s) => s.state === "current") || steps.find((s) => s.state === "open")

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title={`${track.title} - Kingdom Factor`} />

      <div className="max-w-5xl mx-auto grid gap-8 lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="min-w-0 space-y-4 lg:sticky lg:top-4 self-start">
          <TrackRail track={track} steps={steps} progress={progress} certified={!!certification} />
          {canStartOver && <StartOver trackId={track.id} />}
        </aside>

        <main className="min-w-0 space-y-4 order-first lg:order-none">
          {certification && <CertifiedBanner certification={certification} />}

          {continueHref && nextStep && (
            <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
              <div className="bg-kf-black text-white px-6 py-5">
                <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">
                  {progress.completed === 0 ? "Start here" : "Continue"} · Step {nextStep.step} of {steps.length}
                </p>
                <h2 className="font-display text-2xl font-semibold mt-1">{nextStep.title}</h2>
              </div>
              <div className="p-6 space-y-4">
                {nextStep.description && <p className="text-zinc-700">{nextStep.description}</p>}
                <Button asChild variant="gradient" className="min-h-[44px]">
                  <Link href={continueHref}>
                    {progress.completed === 0 ? "Start" : "Continue"}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          )}

          {track.description && <p className="text-sm text-zinc-600">{track.description}</p>}
        </main>
      </div>
    </MemberLayout>
  )
}
